import { Route } from 'vue-router';

/**
 * Ponte entre o funil do ProUni e a matrícula comum.
 *
 * O aluno aprovado no ProUni entra na matrícula por um link pessoal
 * (`/?cupom=<código>`). O cupom é **identificador, não credencial**: quem
 * autentica é a sessão Pocket (cookie `d`), a mesma conta com que ele fez a
 * inscrição. Vazar o cupom não abre nada.
 *
 * O código é o cupom real do JACAD, digitado pela operação: **não tem prefixo**
 * e vale para a chamada inteira. Por isso o texto do cupom não diz se a
 * matrícula é ProUni — quem diz é o tipo da admissão (getter
 * `isProuniEnrollment`, módulo `enrollment`).
 */

/** Nome do query param do link enviado por e-mail ao aluno. */
export const PROUNI_VOUCHER_QUERY_PARAM = 'cupom';

/**
 * Lê o cupom do link. Retorna `undefined` quando o param não existe ou está
 * vazio — é esse `undefined` que garante que a matrícula comum siga idêntica.
 */
export const getProuniVoucherFromRoute = (route?: Route | null): string | undefined => {
  const raw = route?.query?.[PROUNI_VOUCHER_QUERY_PARAM];
  const value = Array.isArray(raw) ? raw[0] : raw;

  if (typeof value !== 'string') return undefined;

  const voucher = value.trim();
  return voucher || undefined;
};

/**
 * Mensagens com que o `exchangeProuniEnrollmentToken` (ug-enrollments) recusa a
 * troca porque o cupom não é ProUni desta conta. Espelho literal de
 * `PROUNI_NO_APPROVED_APPLICATION_MESSAGE` e `PROUNI_VOUCHER_NOT_OWNED_MESSAGE`:
 * o backend não devolve código de erro estável, e o `status` do `HttpException`
 * some do payload em produção.
 */
export const PROUNI_VOUCHER_REFUSAL_MESSAGES = [
  'Esta conta não tem inscrição ProUni aprovada. Entre com a mesma conta Descomplica que você usou na inscrição.',
  'Este cupom não pertence a esta conta. Entre com a mesma conta Descomplica que você usou na inscrição do ProUni.',
];

/**
 * `true` só quando o backend respondeu e recusou o cupom como ProUni desta conta —
 * é o sinal de que `?cupom=` pode ser um cupom de campanha (o código do ProUni é
 * digitado pela operação e não tem prefixo, então o texto não diz nada).
 * Sessão Pocket inválida, rede e qualquer outro erro dão `false`: se o backend
 * mudar a mensagem, o pior caso é voltar ao gate de antes.
 */
export const isProuniVoucherRefusal = (err: unknown): boolean => {
  const graphQLErrors = (err as { graphQLErrors?: { message?: string }[] } | null)?.graphQLErrors;

  return (
    Array.isArray(graphQLErrors) &&
    graphQLErrors.some(({ message }) => PROUNI_VOUCHER_REFUSAL_MESSAGES.includes(message || ''))
  );
};
